import BaseController from "../../controller/BaseController";
import AdminDataService from "../../util/AdminDataService";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Button$PressEvent } from "sap/m/Button";
import { CheckBox$SelectEvent } from "sap/m/CheckBox";
import MessageBox from "sap/m/MessageBox";
import Validator from "../../util/Validator";
import ResourceBundle from "sap/base/i18n/ResourceBundle";

/**
 * @namespace com.reply.leaviosa.controller
 */
export default class UsersController extends BaseController {
    private readonly USER_DIALOG_ID = "DialogCreateUpdateUser";
    private readonly TRANSACTION_DIALOG_ID = "DialogAddTransaction";

    public async onInit(): Promise<void> {
        AdminDataService.getUsers();
        this.getView().setModel(new JSONModel({ roles: {} }), "tempRoles");
    }

    //*************************************************************************************
    // EVENT HANDLERS
    //*************************************************************************************

    public async onCreateUser(): Promise<void> {
        this.getView().setModel(new JSONModel({
            autoApprove: false,
        }), "user");
        await this.openDialog(this.USER_DIALOG_ID);
    }

    async onEdit(oEvent: Button$PressEvent): Promise<void> {

        // reset temp roles
        const tempRolesModel = this.getView().getModel("tempRoles") as JSONModel;
        tempRolesModel.setProperty("/roles", {});

        const selectedUser = oEvent.getSource().getBindingContext("dataModel").getObject();
        const groups = await AdminDataService.getGroupsForUser(selectedUser.userId);

        this.getView().setModel(new JSONModel({
            ...selectedUser,
            groups: groups
        }), "user");

        await this.openDialog(this.USER_DIALOG_ID);
    }

    onTeamChange(oEvent: any): void {
        const value = oEvent.getParameter("value");
        if (value == null) {
            (this.getView().getModel("user") as JSONModel).setProperty("/teamId", null);
        }
    }

    async onCheckBoxSelect(oEvent: CheckBox$SelectEvent): Promise<void> {
        const selected = oEvent.getParameter("selected");
        const groupName = oEvent.getSource().getText();
        const tempRolesModel = this.getView().getModel("tempRoles") as JSONModel;
        const tempRoles = tempRolesModel.getProperty("/roles") || {};

        if (selected) {
            tempRoles[groupName] = true;
        } else {
            tempRoles[groupName] = false;
        }

        tempRolesModel.setProperty("/roles", tempRoles);
    }

    async onSaveDialog(oEvent: Button$PressEvent): Promise<void> {

        const validator = new Validator();
        const valid = validator.validate(oEvent.getSource().getParent().getParent());
        if (!valid) return;

        const userData = (this.getView().getModel("user") as JSONModel).getData();
        const tempRolesModel = this.getView().getModel("tempRoles") as JSONModel;
        const tempRoles = tempRolesModel.getProperty("/roles");

        if (userData.userId) {
            await AdminDataService.updateUser(userData);
        } else {
            await AdminDataService.createUser(userData);
        }
        AdminDataService.getUsers();

        try {
            for (const [groupName, selected] of Object.entries(tempRoles)) {
                if (selected) {
                    await AdminDataService.addUserToGroup(userData.userId, groupName);
                } else {
                    await AdminDataService.removeUserFromGroup(userData.userId, groupName);
                }
            }
        } catch (error) {
            console.error(error);
        }

        this.destroyDialog(this.USER_DIALOG_ID);
    }

    onCloseDialog(oEvent: Button$PressEvent): void {
        this.destroyDialog(this.USER_DIALOG_ID);
    }

    onNavigateUser(oEvent: Button$PressEvent): void {
        const currentUser = oEvent.getSource().getBindingContext("dataModel").getObject() as any;
        this.navTo("user", {
            id: currentUser.userId
        });
    }

    async onDeleteUser(oEvent: Button$PressEvent): Promise<void> {
        const deleteUserConfirmationText = (this.getResourceBundle() as ResourceBundle).getText("deleteUserConfirmation");
        const userId = this.getView().getModel("user").getProperty("/userId");
        MessageBox.confirm(deleteUserConfirmationText, {
            actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
            emphasizedAction: MessageBox.Action.OK,
            onClose: (sAction: any) => {
                if (sAction === MessageBox.Action.OK) {
                    AdminDataService.deleteUser(userId);
                    this.destroyDialog(this.USER_DIALOG_ID);
                }
            },
            dependentOn: this.getView()
        });
    }

    onAddTransaction(oEvent: Button$PressEvent): void {

        this.getView().setModel(new JSONModel({
            type: "ADJUSTMENT",
            userId: this.getView().getModel("user").getProperty("/userId"),
            timestamp: Date.now(),
        }), "transaction");

        this.openDialog(this.TRANSACTION_DIALOG_ID);
    }

    onSaveTransaction(): void {

        const validator = new Validator();
        const valid = validator.validate(this.byId("DialogAddTransaction"));
        if (!valid) return;

        const transactionData = (this.getView().getModel("transaction") as JSONModel).getData();
        AdminDataService.createTransaction(transactionData);
        this.destroyDialog(this.TRANSACTION_DIALOG_ID);

    }

    onCancelTransaction(): void {
        this.destroyDialog(this.TRANSACTION_DIALOG_ID);
    }
}
